import { Button, CircularProgress, Typography } from '@mui/material'
import { useEffect, useState, useRef, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import undescore from 'underscore'
import clsx from 'clsx'
import { useDebouncedCallback } from '@workspace/frontend-kit'
import { ConsultationDtoIds, useGetIdsHealthcareservicesHcsIdConsultations } from '@toktokdoctech/tokapi-client'
import useCurrentHcs from '../../../hooks/useCurrentHcs'
import PlannedConsultationCard from './PlannedConsultationCard'
import PlannedConsultationHeader from './PlannedConsultationHeader'
import useStyles from './_styles'
import useButtonStyles from '../../../styles/buttonsStyles'
import PageContent from '../../Common/pageContent/PageContent'
import PageContentBody from '../../Common/pageContent/PageContentBody'
import { ConsultationsFilters, PlannedConsultationCardListProps, RequiredExpertsFilter } from './_props'
import { useAddLogMutation } from '../../../api/mutations/__generated__/addLog.gql'
import useCurrentUser from '../../../hooks/useCurrentUser'
import MessageState from '../../MessageState/MessageState'
import { DisabledConsultationImage, EmptyPlannedConsultationImage } from '../../../assets/images/image'

const getUniqueRequiredExperts = (consultations: ConsultationDtoIds[]) =>
    consultations.reduce(
        (acc: RequiredExpertsFilter[], curr: ConsultationDtoIds) => [
            ...acc,
            ...(curr.requiredExpert?.id && !acc.find((x: RequiredExpertsFilter) => x.id === curr.requiredExpert?.id)
                ? [
                      {
                          id: curr.requiredExpert.id,
                          name: curr.requiredExpert.fullName,
                      },
                  ]
                : []),
        ],
        [],
    )

const PlannedConsultationCardList = ({ isPreview = false }: PlannedConsultationCardListProps) => {
    const classes = useStyles()
    const buttonsClasses = useButtonStyles()
    const healthCareService = useCurrentHcs()
    const currentUser = useCurrentUser()

    const [limit, setLimit] = useState(isPreview ? 4 : 8)
    const [minContainerHeight, setMinContainerHeight] = useState(0)
    const [search, setSearch] = useState('')
    const [filters, setFilters] = useState<ConsultationsFilters>({})
    const [searchValue, setSearchValue] = useState('')

    const debouncedSearch = useDebouncedCallback((value: string) => setSearchValue(value), 300)

    const cardsContainerRef = useRef<HTMLDivElement>(null)

    const [sendLog] = useAddLogMutation()

    const { data, isLoading } = useGetIdsHealthcareservicesHcsIdConsultations(healthCareService.id, {
        ...filters,
        limit,
        patientName: searchValue,
    })

    const hasFilters = useMemo(
        () => searchValue !== '' || !!filters.requiredExpertId || !!filters.startDate || !!filters.endDate,
        [filters.endDate, filters.startDate, filters.requiredExpertId, searchValue],
    )

    const handlePageHeightChange = useCallback(
        () =>
            undescore.debounce(() => {
                if (hasFilters) {
                    // clears container min height when filters change
                    setMinContainerHeight(0)
                }
                if (cardsContainerRef.current && !isLoading) {
                    // keeps previous container height while loading new ones to avoid back on top
                    setMinContainerHeight(cardsContainerRef.current.offsetHeight)
                }
            }, 300),
        [isLoading, hasFilters, cardsContainerRef],
    )

    useEffect(() => {
        handlePageHeightChange()
    }, [isLoading, hasFilters, handlePageHeightChange])

    useEffect(() => {
        window.onresize = () => setMinContainerHeight(0)
    }, [])

    useEffect(() => {
        debouncedSearch(search)
    }, [search, debouncedSearch])

    useEffect(() => {
        sendLog({
            variables: {
                input: {
                    hcsId: healthCareService.id,
                    fromApp: 'IDS',
                    userId: currentUser.id,
                    message: `[PlannedConsultationCardList] Accès liste téléconsultation planifiée`,
                    logLevel: 'INFO',
                },
            },
        })
    }, [healthCareService.id, sendLog, currentUser.id])

    const consultations = data?.consultations ?? []
    const totalConsultations = data?.total ?? 0

    const requiredExperts = getUniqueRequiredExperts(consultations)

    const handleFiltersChange = (newFilters: Partial<ConsultationsFilters>) => {
        setFilters({
            ...filters,
            ...newFilters,
        })
    }

    /**
     * @description the handler that triggers when we reset filters
     * @returns {void}
     */
    const handleResetFilter = () => {
        setFilters({})
        // setCurrentPage(0)
    }

    const handleLoadMore = () => {
        setLimit(limit + 4)
    }

    const consultationLengthCount = `Vous avez vu ${consultations.length} sur ${totalConsultations} téléconsultations planifiées.`

    const renderLoadMore = () => (
        <div className={classes.PlannedConsultationCardsNoPreviewBottom}>
            {consultations.length < totalConsultations && (
                <div className={classes.PlannedConsultationCardsCountContainer}>
                    <Typography className={classes.PlannedConsultationCardsCount}>{consultationLengthCount}</Typography>
                    <div className={classes.PlannedConsultationCardsCountContainerLoadbar}>
                        <div
                            className={classes.PlannedConsultationCardsCountContainerLoadbarProgress}
                            style={{
                                width: `${(consultations.length * 100) / totalConsultations}%`,
                            }}
                        />
                    </div>
                </div>
            )}

            {consultations.length < totalConsultations && (
                <Button onClick={handleLoadMore} className={buttonsClasses.ButtonBlack}>
                    Voir plus d'actes
                </Button>
            )}
            {consultations.length === totalConsultations && (
                <Typography className={classes.PlannedConsultationCardsEndOfList}>Fin de la liste</Typography>
            )}
        </div>
    )

    const renderContent = () => {
        if (!healthCareService.isConsultationEnabled) {
            return (
                <MessageState
                    text="Cette fonctionnalité est désactivée sur votre établissement. Pour en savoir plus, contactez le support TokTokDoc."
                    image={<DisabledConsultationImage/>}
                />
            )
        }
        if (consultations.length > 0) {
            return (
                <>
                    <div className={classes.PlannedConsultationCardsInner}>
                        {consultations.map((consultation) => (
                            <PlannedConsultationCard consultation={consultation} isPreview={isPreview} key={consultation.id} />
                        ))}
                    </div>
                    <div
                        className={clsx(
                            classes.PlannedConsultationCardsButonContainer,
                            !isPreview && classes.PlannedConsultationCardsButonContainerMore,
                        )}
                    >
                        {isPreview && (
                            <Button
                                component={Link}
                                to="/planned-consultations"
                                className={buttonsClasses.ButtonTextBlackUnderlined}
                            >
                                Voir tout
                            </Button>
                        )}
                        {!isPreview && renderLoadMore()}
                    </div>
                </>
            )
        }
        return (
            <MessageState
                text="Aucune téléconsultation programmée à venir, les prochaines s’afficheront ici."
                image={<EmptyPlannedConsultationImage/>}
            />
        )
    }
    const renderLoader = () => (
        <div className={classes.PlannedConsultationCardListLoading}>
            <CircularProgress size="4.5rem" className={classes.PlannedConsultationCardListLoadingLoader} />
            <Typography className={classes.PlannedConsultationCardListLoadingMessage}>
                Chargement des téléconsulations planifiées
            </Typography>
        </div>
    )

    const renderPreviewTitle = () => {
        let titleText
        if (!healthCareService.isConsultationEnabled) {
            titleText = '0 téléconsultation à venir'
        } else if (totalConsultations > 0) {
            titleText = `${consultations.length} sur ${totalConsultations} ${
                totalConsultations === 1 ? 'affichée' : 'affichées'
            }`
        }
        return (
            <div className={classes.PlannedConsultationHeaderTitleContainer}>
                <Typography className={classes.PlannedConsultationHeaderTitle}>
                    Téléconsultations planifiées
                </Typography>

                {totalConsultations > 0 ? (
                    <div className={classes.PlannedConsultationHeaderTitleContainerCount}>
                        <Typography className={classes.PlannedConsultationCardListCountText}>{titleText}</Typography>
                        {healthCareService.isConsultationEnabled && (
                            <Button
                                component={Link}
                                to="/planned-consultations"
                                className={classes.PlannedConsultationHeaderTitleContainerCountButton}
                            >
                                Voir tout
                            </Button>
                        )}
                    </div>
                ) : null}
            </div>
        )
    }

    return (
        <PageContent>
            <PlannedConsultationHeader
                isPreview={isPreview}
                setSearch={setSearch}
                search={search}
                requiredExperts={requiredExperts}
                handleFiltersChange={handleFiltersChange}
                filters={filters}
                handleResetFilter={handleResetFilter}
                previewTitle={renderPreviewTitle()}
                renderLeftHeader
                disabled={!healthCareService.isConsultationEnabled}
            />
            <PageContentBody>
                <div
                    className={isPreview ? classes.PlannedConsultationCardListMainContainer : undefined}
                    ref={cardsContainerRef}
                    style={{
                        minHeight: `${minContainerHeight}px`,
                    }}
                >
                    {isLoading ? renderLoader() : renderContent()}
                </div>
            </PageContentBody>
        </PageContent>
    )
}

export default PlannedConsultationCardList
